import React, { useEffect, useState } from "react";
import { Badge, Button, ButtonGroup, Card, Col, Dropdown, DropdownButton, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useRouteLoaderData } from "react-router-dom";

import { useRequest } from "@components/AppHook";
import { DevForm, FormFieldGroup } from "@components/Forms/Fields";
import { DevModal } from "@components/Helpers";
import { SimplePaginator } from "@components/Paginator";
import { buildQS } from "@components/Utils";

const ConfirmDeleteModal = ({ idsToDelete, setIdsToDelete, showModal, setShowModal, fetchDataHandle }) => {
    const { t } = useTranslation();
    const { ext_api_post } = useRequest();

    const deleteDataHandle = (ev) => {
        ext_api_post(ev, `/community/moderation_wallpost_delete`, {
            params: { id: idsToDelete.join(",") },
            then: (resp) => {
                if (resp.status === "ok") {
                    fetchDataHandle();
                    setIdsToDelete([]);
                    setShowModal(false);
                }
            },
        });
    };
    return (
        <DevModal
            show={showModal}
            onHide={() => setShowModal(false)}
            body={t("Подтвердите удаление")}
            footer={
                <>
                    <Button variant="light" onClick={() => setShowModal(false)}>
                        {t("Закрыть")}
                    </Button>
                    <DevForm.Button variant="danger" name="delete" onClick={deleteDataHandle}>
                        {t("Удалить")}
                    </DevForm.Button>
                </>
            }
        />
    );
};

export default function ModerateProfilePostsScreen() {
    const { t } = useTranslation();
    const [data, setData] = useState();
    const [queryParams, setQueryParams] = useState({});
    const [idsToDelete, setIdsToDelete] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const commonInfo = useRouteLoaderData("commonInfo");
    const { ext_api_get } = useRequest();

    if (!("page" in queryParams)) {
        queryParams.page = 1;
    }

    const toggleAllIdsToDelete = () => {
        if (idsToDelete.length === data?.feed.length) {
            setIdsToDelete([]);
        } else {
            setIdsToDelete(data?.feed.map((i) => i.post_id));
        }
    };

    const toggleIdToDelete = (id) => {
        if (idsToDelete.includes(id)) {
            setIdsToDelete(idsToDelete.filter((i) => i !== id));
        } else {
            setIdsToDelete([...idsToDelete, id]);
        }
    };

    const fetchDataHandle = () => {
        const qs = buildQS({ ...queryParams, page: queryParams.page });
        ext_api_get("search", `/community/moderation_wallpost_get?${qs}`, {
            then: (resp) => {
                if (resp.status === "ok") {
                    setData(resp.data);
                }
            },
        });
    };

    const handleFormChange = (obj) => {
        const newQueryParams = { ...queryParams };
        Object.entries(obj).forEach(([k, v]) => {
            newQueryParams[k] = v;
        });
        setQueryParams(newQueryParams);
    };

    useEffect(() => fetchDataHandle(), [queryParams.page]);

    return (
        <Card>
            <Card.Header>
                {t("Модерация постов профиля")}
                <div className="dmg-card-header-tools">
                    <DropdownButton as={ButtonGroup} size="sm" variant="dark" title={t("С выбранными")}>
                        <Dropdown.Item onClick={() => setShowModal(true)}>{t("Удалить")}</Dropdown.Item>
                    </DropdownButton>
                </div>
                <ConfirmDeleteModal
                    setShowModal={setShowModal}
                    showModal={showModal}
                    setIdsToDelete={setIdsToDelete}
                    idsToDelete={idsToDelete}
                    fetchDataHandle={fetchDataHandle}
                />
            </Card.Header>
            <Card.Body>
                <Row>
                    <Col>
                        <FormFieldGroup hideLabel={true} asRow={false}>
                            <DevForm.TextInput
                                value={queryParams.login}
                                placeholder={t("Поиск по login")}
                                onChange={(ev) => handleFormChange({ login: ev.target.value })}
                            />
                        </FormFieldGroup>
                    </Col>
                    <Col>
                        <FormFieldGroup hideLabel={true} asRow={false}>
                            <DevForm.TextInput
                                value={queryParams.slug}
                                placeholder={t("Поиск по slug")}
                                onChange={(ev) => handleFormChange({ slug: ev.target.value })}
                            />
                        </FormFieldGroup>
                    </Col>
                    <Col>
                        <FormFieldGroup hideLabel={true} asRow={false}>
                            <DevForm.TextInput
                                value={queryParams.ac_id}
                                placeholder={t("Поиск по ac_id")}
                                onChange={(ev) => handleFormChange({ ac_id: ev.target.value })}
                            />
                        </FormFieldGroup>
                    </Col>
                    <Col>
                        <FormFieldGroup hideLabel={true} asRow={false}>
                            <DevForm.DateRangePicker
                                value={[
                                    (queryParams.date_added__gte || "").split(" ")[0],
                                    (queryParams.date_added__lte || "").split(" ")[0],
                                ]}
                                placeholder={t("Дата")}
                                onChange={(rng) => {
                                    handleFormChange({
                                        date_added__gte: `${rng[0]} 00:00:00`,
                                        date_added__lte: `${rng[1]} 23:59:59`,
                                    });
                                }}
                            />
                        </FormFieldGroup>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <DevForm.Button variant="primary" name="search" onClick={fetchDataHandle}>
                            {t("Поиск")}
                        </DevForm.Button>
                    </Col>
                </Row>
                <Table hover responsive className="dmg-table mt-5">
                    <thead>
                        <tr>
                            <th>
                                <DevForm.Checkbox
                                    checked={idsToDelete.length === data?.feed.length}
                                    onChange={() => toggleAllIdsToDelete()}
                                />
                            </th>
                            <th>{t("Автор")}</th>
                            <th style={{ width: "450px" }}>{t("Текст")}</th>
                            <th>{t("Забанен")}</th>
                            <th>{t("Дата")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data &&
                            data.feed.map((item) => (
                                <tr key={item.post_id}>
                                    <td>
                                        <DevForm.Checkbox
                                            checked={idsToDelete.includes(item.post_id)}
                                            onChange={() => toggleIdToDelete(item.post_id)}
                                        />
                                    </td>
                                    <td>
                                        {item.user_author.slug ? (
                                            <>
                                                <a
                                                    href={`https://${commonInfo.domain_profile}/profile/${item.user_author.slug}/`}
                                                    target="_blank"
                                                >
                                                    {item.user_author.nick}
                                                </a>{" "}
                                            </>
                                        ) : (
                                            item.user_author.nick
                                        )}
                                    </td>
                                    <td>{item.entity.text}</td>
                                    <td>
                                        {item.user_author.is_banned ? (
                                            <Badge variant="danger">{t("Да")}</Badge>
                                        ) : (
                                            <Badge variant="success">{t("Нет")}</Badge>
                                        )}
                                    </td>
                                    <td>{new Date(item.ts * 1000).toLocaleString()}</td>
                                </tr>
                            ))}
                    </tbody>
                </Table>
            </Card.Body>
            <SimplePaginator
                curPage={data?.feed.length > 0 ? queryParams.page : null}
                nextPage={data?.next_page || 1}
                prevPage={data?.prev_page || 1}
                changePage={(pageNumber) => setQueryParams((p) => ({ ...p, page: pageNumber }))}
            />
        </Card>
    );
}
